import { useState } from 'react'
import cx from 'classnames'
import { groupBy } from 'es-toolkit'
import { CaretDownIcon } from '@/common/components/icons'
import { motion, AnimatePresence } from 'motion/react'
import { Alert } from '@/common/components/Alert/Alert'
import useI18n from '@/shared/hooks/usei18n'
import { importedGoodsGRRErrors } from '../hooks/importedGoodsGRR'

type ValidationError = {
  key: string
  row: number
  field: string
  fieldName?: string
  value: any
}

type ValidationListType = 'error' | 'warning'

const listConfig: Record<
  ValidationListType,
  {
    alertType: 'error' | 'warning'
    titleKey: string
    summaryKey: string
  }
> = {
  error: {
    alertType: 'error',
    titleKey: 'errorsTitle',
    summaryKey: 'errorsSummary'
  },
  warning: {
    alertType: 'warning',
    titleKey: 'warningsTitle',
    summaryKey: 'warningsSummary'
  }
}

export function GoodsReceivedReportValidationErrorsList({
  type = 'error',
  items
}: {
  type?: ValidationListType
  items: ValidationError[]
}) {
  const { t } = useI18n()

  if (items.length === 0) {
    return null
  }

  const groupedItems = groupBy(items, item => item.key)
  const uniqueRows = new Set(items.map(item => item.row)).size
  const config = listConfig[type]

  return (
    <div className="flex flex-col gap-4">
      {/* Summary Header */}
      <Alert type={config.alertType}>
        <p className="font-semibold">
          {t(`cbamReporting:importedGoodsUpload.validationErrors.${config.titleKey}`)}
        </p>
        <p className="text-sm mt-1">
          {t(`cbamReporting:importedGoodsUpload.validationErrors.${config.summaryKey}`, {
            count: items.length,
            totalRows: uniqueRows
          })}
        </p>
      </Alert>

      {/* Groups */}
      <div className="flex flex-col gap-2">
        {Object.entries(groupedItems).map(([itemKey, itemList]) => (
          <GoodsReceivedReportValidationGroup
            key={itemKey}
            type={type}
            itemKey={itemKey}
            items={itemList!}
          />
        ))}
      </div>
    </div>
  )
}

const groupStyles: Record<
  ValidationListType,
  {
    border: string
    hoverBg: string
    focusRing: string
    iconColor: string
    titleColor: string
    badgeBg: string
    badgeText: string
    descriptionColor: string
    contentBg: string
    contentBorder: string
    itemBorder: string
    itemTitleColor: string
    buttonColor: string
    buttonHoverColor: string
  }
> = {
  error: {
    border: 'border-red-400',
    hoverBg: 'hover:bg-red-50',
    focusRing: 'focus:ring-red-300',
    iconColor: 'text-red-500',
    titleColor: 'text-red-900',
    badgeBg: 'bg-red-200',
    badgeText: 'text-red-800',
    descriptionColor: 'text-red-700',
    contentBg: 'bg-red-25',
    contentBorder: 'border-red-200',
    itemBorder: 'border-red-200',
    itemTitleColor: 'text-red-900',
    buttonColor: 'text-red-700',
    buttonHoverColor: 'hover:text-red-900'
  },
  warning: {
    border: 'border-yellow-500',
    hoverBg: 'hover:bg-yellow-50',
    focusRing: 'focus:ring-yellow-300',
    iconColor: 'text-yellow-500',
    titleColor: 'text-yellow-900',
    badgeBg: 'bg-yellow-200',
    badgeText: 'text-yellow-800',
    descriptionColor: 'text-yellow-700',
    contentBg: 'bg-yellow-25',
    contentBorder: 'border-yellow-200',
    itemBorder: 'border-yellow-200',
    itemTitleColor: 'text-yellow-900',
    buttonColor: 'text-yellow-700',
    buttonHoverColor: 'hover:text-yellow-900'
  }
}

function GoodsReceivedReportValidationGroup({
  type,
  itemKey,
  items
}: {
  type: ValidationListType
  itemKey: string
  items: ValidationError[]
}) {
  const initialDisplayLimit = 20

  const [isExpanded, setIsExpanded] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const { t } = useI18n()
  let translationKey = itemKey in importedGoodsGRRErrors ? itemKey : 'GENERIC_ERROR'

  const title = t(
    `cbamReporting:importedGoodsUpload.validationErrors.errors.${translationKey}.title`
  )
  const description = t(
    `cbamReporting:importedGoodsUpload.validationErrors.errors.${translationKey}.description`
  )
  const styles = groupStyles[type]

  const hasMore = items.length > initialDisplayLimit
  const displayedItems = showAll || !hasMore ? items : items.slice(0, initialDisplayLimit)

  const handleExpand = () => {
    setIsExpanded(!isExpanded)
    if (!isExpanded) {
      setShowAll(false)
    }
  }

  return (
    <div className={cx('border rounded-xs overflow-hidden bg-white', styles.border)}>
      {/* Clickable Header */}
      <button
        onClick={handleExpand}
        className={cx(
          'w-full flex items-start gap-3 px-3.5 py-3 text-left transition-colors',
          styles.hoverBg,
          'focus:outline-none focus:ring-2 focus:ring-inset',
          styles.focusRing
        )}
      >
        {/* Caret Icon */}
        <CaretDownIcon
          className={cx(
            'size-6 transition-transform mt-1',
            styles.iconColor,
            isExpanded && 'rotate-180'
          )}
        />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cx('font-medium', styles.titleColor)}>{title}</span>
            <span
              className={cx('text-xs px-2 py-0.5 rounded-xs', styles.badgeBg, styles.badgeText)}
            >
              {t('cbamReporting:importedGoodsUpload.validationErrors.rowsAffected', {
                count: items.length
              })}
            </span>
          </div>
          <p className={cx('text-sm mt-1', styles.descriptionColor)}>{description}</p>
        </div>
      </button>

      {/* Expandable Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className={cx('px-3.5 py-3 border-t', styles.contentBg, styles.contentBorder)}>
              <div className="flex flex-col gap-2">
                {displayedItems.map((item, index) => (
                  <div
                    key={`${item.row}-${item.field}-${index}`}
                    className={cx(
                      'flex items-start gap-4 text-sm px-3 py-2 bg-white rounded border',
                      styles.itemBorder
                    )}
                  >
                    <span
                      className={cx('font-mono font-semibold min-w-[80px]', styles.itemTitleColor)}
                    >
                      {item.row === 0
                        ? t(
                            'cbamReporting:importedGoodsUpload.validationErrors.errorDetails.header'
                          )
                        : t('cbamReporting:importedGoodsUpload.validationErrors.errorDetails.row', {
                            rowNumber: item.row
                          })}
                    </span>
                    <div className="flex-1 flex flex-col gap-1">
                      <span className="text-gray-700">
                        {t(
                          'cbamReporting:importedGoodsUpload.validationErrors.errorDetails.field',
                          {
                            fieldName: item.fieldName ?? item.field
                          }
                        )}
                      </span>
                      {item.value !== null && item.value !== undefined && item.value !== '' ? (
                        <span className="text-gray-600 font-mono text-xs">
                          {t(
                            'cbamReporting:importedGoodsUpload.validationErrors.errorDetails.value',
                            {
                              value: item.value
                            }
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-xs">
                          {t(
                            'cbamReporting:importedGoodsUpload.validationErrors.errorDetails.noValue'
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {hasMore && !showAll && (
                  <button
                    onClick={() => setShowAll(true)}
                    className={cx(
                      'text-sm font-medium py-2 hover:underline',
                      styles.buttonColor,
                      styles.buttonHoverColor
                    )}
                  >
                    {t('cbamReporting:importedGoodsUpload.validationErrors.showMore', {
                      count: items.length - initialDisplayLimit
                    })}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
